import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { cx } from 'cva'
import { Hex } from 'ox'
import type { ReactElement } from 'react'
import { useMemo, useState } from 'react'
import { Amount, Box, Button, Empty, Rows, Token, Tooltip } from 'regen-ui'
import { toast } from 'sonner'
import { formatUnits } from 'viem'
import { useConnection } from 'wagmi'
import ArrowDownToLine from '~icons/lucide/arrow-down-to-line'
import ArrowLeftRight from '~icons/lucide/arrow-left-right'
import ArrowUpFromLine from '~icons/lucide/arrow-up-from-line'
import ChevronDown from '~icons/lucide/chevron-down'
import Globe from '~icons/lucide/globe'
import Lock from '~icons/lucide/lock'
import SendHorizontal from '~icons/lucide/send-horizontal'
import VenetianMask from '~icons/lucide/venetian-mask'
import Wallet from '~icons/lucide/wallet'

import * as Activity from '#/lib/activity.js'
import * as Assets from '#/lib/assets.js'
import { useDialogConnector } from '#/lib/connectors.js'
import { chain as zoneChain } from '#/lib/zone.js'
import {
  PrivateBalancesTag,
  PrivateBalancesTextButton,
  usePrivateBalances,
} from '#/routes/_app/-components/PrivateBalancesControls.js'

const rowHeight = 44
const expandedRowHeight = 36

/** Wallet card showing a list of token balances with fiat/crypto toggle. */
export function BalancesCard(props: BalancesCard.Props) {
  const { assets, chainId, emptyAction, loading } = props
  const privateBalances = usePrivateBalances()

  // The worker returns one row per token with parent-only `balance`
  // and slim `zones[]`. The user cares about gross holdings, so filter
  // on `balance + sum(zones)`.
  // Sort highest-balance first using `Number(formatUnits(...))` so tokens
  // with different decimals compare on their human-readable amount rather
  // than raw units.
  const rows = useMemo(
    () =>
      assets
        .filter((a) => a.verified !== false && grossBalance(a) > 0n)
        .slice()
        .sort((a, b) => {
          const av = Number(formatUnits(grossBalance(a), a.decimals))
          const bv = Number(formatUnits(grossBalance(b), b.decimals))
          if (av === bv) return a.symbol.localeCompare(b.symbol)
          return bv - av
        })
        .slice(0, 4),
    [assets],
  )
  const compact = !loading && rows.length > 0
  const alignTrailing = rows.some((asset) => asset.zones?.length)

  return (
    <Box
      className={cx(
        'relative flex flex-col max-sm:-mx-4 sm:-mx-8 md:mx-0 max-md:rounded-none max-md:border-x-0',
        !compact && 'min-h-[286px]',
      )}
      padding={0}
    >
      <div className="flex items-center justify-between gap-3 px-5 pt-5 pb-3">
        <div className="flex min-w-0 items-center gap-2">
          <h2 className="text-label-16 font-medium text-foreground">Balances</h2>
          <PrivateBalancesTag privacy={privateBalances} />
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <PrivateBalancesTextButton
            className="-mr-1.5 shrink-0 max-sm:-mr-3 max-sm:px-3"
            privacy={privateBalances}
          />
          <Button.Text
            className="-mr-1.5"
            render={<Link search={(prev: Record<string, unknown>) => prev} to="/balances" />}
          >
            View all
          </Button.Text>
        </div>
      </div>
      <Rows className="pb-1 [&_[data-rows-divider]]:hidden" variant="surface">
        {loading ? (
          <>
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </>
        ) : rows.length === 0 ? (
          <Empty
            action={emptyAction}
            className="absolute inset-0 min-h-0 px-5 py-0"
            icon={<Wallet />}
            title="No balances yet"
          >
            Deposit funds to start using your wallet.
          </Empty>
        ) : (
          rows.map((asset) => (
            <BalanceGroup
              alignTrailing={alignTrailing}
              asset={asset}
              chainId={chainId}
              key={asset.address}
            />
          ))
        )}
      </Rows>
    </Box>
  )
}

function SkeletonRow() {
  return (
    <Rows.Row height={rowHeight}>
      <div className="size-6 animate-pulse rounded-identicon bg-skeleton" />
      <div className="h-4 w-20 animate-pulse rounded-body bg-skeleton" />
      <div className="ml-auto h-4 w-16 animate-pulse rounded-body bg-skeleton" />
    </Rows.Row>
  )
}

function BalanceGroup(props: {
  alignTrailing: boolean
  asset: BalancesCard.Asset
  chainId: number
}) {
  const { alignTrailing, asset, chainId } = props
  const [expanded, setExpanded] = useState(false)
  const zones = asset.zones ?? []
  const hasZones = zones.length > 0
  const gross = grossBalance(asset)

  const zoneSum = zones.reduce((sum, z) => sum + BigInt(z.balance), 0n)
  const hasPrivate = zoneSum > 0n

  // No zones → plain row, no expand machinery.
  if (!hasZones)
    return (
      <BalanceRow
        alignTrailing={alignTrailing}
        asset={asset}
        chainId={chainId}
        gross={gross}
        hasPrivate={hasPrivate}
      />
    )

  return (
    <>
      <BalanceRow
        alignTrailing={alignTrailing}
        asset={asset}
        chainId={chainId}
        expanded={expanded}
        gross={gross}
        hasPrivate={hasPrivate}
        onClick={() => setExpanded((open) => !open)}
      />
      {expanded && (
        <>
          <PrivacyRow asset={asset} balance={BigInt(asset.balance)} icon={Globe} label="Public" />
          <PrivacyRow actions asset={asset} balance={zoneSum} icon={Lock} label="Private" />
        </>
      )}
    </>
  )
}

function grossBalance(asset: BalancesCard.Asset) {
  return (asset.zones ?? []).reduce((sum, z) => sum + BigInt(z.balance), BigInt(asset.balance))
}

function BalanceRow(props: {
  alignTrailing: boolean
  asset: BalancesCard.Asset
  chainId: number
  expanded?: boolean | undefined
  gross: bigint
  hasPrivate?: boolean
  onClick?: (() => void) | undefined
}) {
  const { alignTrailing, asset, chainId, expanded, gross, hasPrivate, onClick } = props
  const connector = useDialogConnector()
  const queryClient = useQueryClient()
  const { address: account } = useConnection()
  const swappable = asset.address.toLowerCase().startsWith('0x20c0')

  const swap = useMutation({
    mutationFn: async () => {
      const provider = await connector.getProvider()
      await provider.request({
        method: 'wallet_swap',
        params: [{ token: asset.address, type: 'sell' }],
      } as never)
    },
    onSuccess: () => {
      toast.success('Swap confirmed.')
      if (account) {
        queryClient.invalidateQueries({ queryKey: Assets.queryKey(account) })
        queryClient.invalidateQueries({ queryKey: Activity.queryKey(account) })
      }
    },
  })

  const send = useMutation({
    mutationFn: async () => {
      const provider = await connector.getProvider()
      return (await provider.request({
        method: 'wallet_transfer',
        params: [{ editable: true, token: asset.address }],
      } as never)) as { chainId: number; receipt: { logs: readonly Assets.setReceiptData.Log[] } }
    },
    onSuccess: (result) => {
      toast.success('Transaction confirmed.')
      if (account)
        Assets.setReceiptData({
          account,
          chainId: result.chainId,
          queryClient,
          receipt: result.receipt,
        })
      if (account) queryClient.invalidateQueries({ queryKey: Activity.queryKey(account) })
    },
  })

  const amount = (
    <Amount
      align="right"
      amount={{
        amount: Hex.fromNumber(gross),
        decimals: asset.decimals,
        formatted: formatUnits(gross, asset.decimals),
        symbol: asset.symbol,
      }}
      className="min-w-0"
    />
  )

  const actions = (
    <div className="flex items-center justify-end gap-0" onClick={(e) => e.stopPropagation()}>
      <Tooltip label={swappable ? `Swap ${asset.symbol}` : `${asset.symbol} cannot be swapped`}>
        <Button.Icon
          aria-label={swappable ? `Swap ${asset.symbol}` : `${asset.symbol} cannot be swapped`}
          className="disabled:cursor-not-allowed disabled:opacity-25 disabled:hover:opacity-25"
          disabled={!swappable}
          onClick={() => swap.mutate()}
          size="md"
          variant="plain"
        >
          <ArrowLeftRight />
        </Button.Icon>
      </Tooltip>
      <Tooltip label={`Send ${asset.symbol}`}>
        <Button.Icon
          aria-label={`Send ${asset.symbol}`}
          className="-ml-2"
          onClick={() => send.mutate()}
          size="md"
          variant="plain"
        >
          <SendHorizontal />
        </Button.Icon>
      </Tooltip>
    </div>
  )

  const trailing = (() => {
    if (alignTrailing)
      return (
        <BalanceTrailing
          actions={actions}
          amount={amount}
          expanded={expanded}
          onClick={onClick}
          symbol={asset.symbol}
        />
      )

    return (
      <div className="flex shrink-0 items-center gap-0">
        {amount}
        {actions}
      </div>
    )
  })()

  const content = (
    <>
      <Token address={asset.address} chainId={chainId} className="size-6" />
      <div className="flex min-w-0 flex-1 items-center gap-1.5">
        <p className="truncate text-label-13 text-foreground">{asset.name}</p>
        {hasPrivate && (
          <Tooltip label="Has private balance">
            <VenetianMask
              aria-label="Has private balance"
              className="size-3.5 text-foreground-secondary"
            />
          </Tooltip>
        )}
      </div>
      {trailing}
    </>
  )

  return <Rows.Row height={rowHeight}>{content}</Rows.Row>
}

function BalanceTrailing(props: {
  actions: ReactElement
  amount: ReactElement
  expanded?: boolean | undefined
  onClick?: (() => void) | undefined
  symbol?: string | undefined
}) {
  const { actions, amount, expanded, onClick, symbol } = props
  const label = (() => {
    if (!symbol || expanded === undefined) return undefined
    if (expanded) return `Hide locations for ${symbol}`
    return `Show locations for ${symbol}`
  })()

  return (
    <div className="flex w-48 shrink-0 items-center justify-end gap-1 min-[380px]:w-52 sm:w-64 sm:gap-2">
      <div className="flex min-w-0 flex-1 justify-end">{amount}</div>
      <div className="flex w-14 justify-end sm:w-[72px]">{actions}</div>
      <div className="flex w-8 justify-center">
        {label && onClick ? (
          <Tooltip label={label}>
            <Button.Icon
              aria-expanded={expanded}
              aria-label={label}
              onClick={onClick}
              size="md"
              variant="plain"
            >
              <ChevronDown
                aria-hidden="true"
                className="size-4 transition-transform data-[expanded=true]:rotate-180"
                data-expanded={expanded}
              />
            </Button.Icon>
          </Tooltip>
        ) : (
          <div aria-hidden className="size-8" />
        )}
      </div>
    </div>
  )
}

function PrivacyRow(props: {
  /** When true, render deposit + withdraw action buttons in the trailing icon slot. */
  actions?: boolean | undefined
  asset: BalancesCard.Asset
  balance: bigint
  icon: React.ComponentType<{ className?: string }>
  label: string
}) {
  const { actions, asset, balance, icon: Icon, label } = props
  const canWithdraw = balance > 0n
  const connector = useDialogConnector()
  const queryClient = useQueryClient()

  const { address: account, chainId: parentChainId } = useConnection()

  const deposit = useMutation({
    mutationFn: async () => {
      const provider = await connector.getProvider()
      const result = (await provider.request({
        method: 'wallet_depositZone',
        params: [{ token: asset.address }],
      } as never)) as { receipt: { logs: readonly Assets.setReceiptData.Log[] } }
      return result.receipt
    },
    onSuccess: (receipt) => {
      toast.success('Deposit confirmed.')
      if (account && parentChainId)
        Assets.setReceiptData({ account, chainId: parentChainId, queryClient, receipt })
      if (account) queryClient.invalidateQueries({ queryKey: Activity.queryKey(account) })
    },
  })

  const withdraw = useMutation({
    mutationFn: async () => {
      const provider = await connector.getProvider()
      const result = (await provider.request({
        method: 'wallet_withdrawZone',
        params: [{ token: asset.address }],
      } as never)) as { receipt: { logs: readonly Assets.setReceiptData.Log[] } }
      return result.receipt
    },
    onSuccess: (receipt) => {
      toast.success('Withdrawal submitted.')
      if (account) Assets.setReceiptData({ account, chainId: zoneChain.id, queryClient, receipt })
      if (account) queryClient.invalidateQueries({ queryKey: Activity.queryKey(account) })
    },
  })

  return (
    <Rows.Row className="text-foreground-secondary" height={expandedRowHeight}>
      <div aria-hidden className="flex size-6 shrink-0 items-center justify-center">
        <Icon className="size-3.5" />
      </div>
      <p className="min-w-0 flex-1 truncate text-label-13">{label}</p>
      <BalanceTrailing
        actions={
          <div className="flex items-center justify-end gap-0">
            {actions ? (
              <>
                <Tooltip label="Deposit to private balance">
                  <Button.Icon
                    aria-label="Deposit to private balance"
                    onClick={() => deposit.mutate()}
                    size="md"
                    variant="plain"
                  >
                    <ArrowDownToLine />
                  </Button.Icon>
                </Tooltip>
                {canWithdraw ? (
                  <Tooltip label="Withdraw from private balance">
                    <Button.Icon
                      aria-label="Withdraw from private balance"
                      className="-ml-2"
                      onClick={() => withdraw.mutate()}
                      size="md"
                      variant="plain"
                    >
                      <ArrowUpFromLine />
                    </Button.Icon>
                  </Tooltip>
                ) : (
                  <div aria-hidden className="-ml-2 size-8" />
                )}
              </>
            ) : (
              <>
                <div aria-hidden className="size-8" />
                <div aria-hidden className="-ml-2 size-8" />
              </>
            )}
          </div>
        }
        amount={
          <Amount
            align="right"
            amount={{
              amount: Hex.fromNumber(balance),
              decimals: asset.decimals,
              formatted: formatUnits(balance, asset.decimals),
              symbol: asset.symbol,
            }}
            className="min-w-0"
          />
        }
      />
    </Rows.Row>
  )
}

export declare namespace BalancesCard {
  type Asset = {
    address: `0x${string}`
    /** Parent-chain-only balance (raw token units). */
    balance: string
    decimals: number
    fiat: { balance: string; currency: string } | undefined
    name: string
    symbol: string
    verified?: boolean | undefined
    /** Per-zone holdings of the same token. */
    zones?: readonly { balance: string; zoneId: number }[] | undefined
  }

  type Props = {
    assets: readonly Asset[]
    chainId: number
    emptyAction?: ReactElement | undefined
    loading?: boolean
  }
}
